import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

import { cn } from '@frontend/lib/cn';
import { IconButton } from './Button';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Accessible dialog: focus is trapped while open, restored on close, Escape closes,
 * and the backdrop click closes unless `dismissible` is false (used for anything that
 * would lose typed work).
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  dismissible = true,
  'data-testid': testId,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  dismissible?: boolean;
  'data-testid'?: string;
}): JSX.Element | null {
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape' && dismissible) {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const panel = panelRef.current;
      if (panel === null) return;
      const focusable = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (element) => element.offsetParent !== null,
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (first === undefined || last === undefined) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [dismissible, onClose],
  );

  useEffect(() => {
    if (!open) return undefined;
    restoreRef.current = document.activeElement as HTMLElement | null;
    document.addEventListener('keydown', handleKeyDown, true);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const timer = window.setTimeout(() => {
      const panel = panelRef.current;
      const target = panel?.querySelector<HTMLElement>(FOCUSABLE) ?? panel;
      target?.focus();
    }, 0);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.body.style.overflow = previousOverflow;
      window.clearTimeout(timer);
      restoreRef.current?.focus();
    };
  }, [open, handleKeyDown]);

  if (!open) return null;

  const width =
    size === 'sm' ? 'max-w-md' : size === 'lg' ? 'max-w-3xl' : size === 'xl' ? 'max-w-5xl' : 'max-w-xl';

  return createPortal(
    <div className="fixed inset-0 z-modal flex items-start justify-center overflow-y-auto p-4 tablet:p-8">
      <div
        className="fixed inset-0 animate-fade-in bg-surface-overlay"
        onClick={dismissible ? onClose : undefined}
        aria-hidden
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        tabIndex={-1}
        data-testid={testId ?? 'modal'}
        className={cn(
          'surface-card relative my-auto w-full animate-scale-in shadow-xl',
          width,
        )}
      >
        <div className="flex items-start justify-between gap-3 border-b border-line px-4 py-3">
          <div className="min-w-0">
            <h2 className="text-section font-semibold text-content">{title}</h2>
            {description !== undefined && (
              <p className="mt-0.5 text-body text-content-tertiary">{description}</p>
            )}
          </div>
          {dismissible && (
            <IconButton
              variant="ghost"
              size="sm"
              label="Close"
              icon={<X className="h-4 w-4" />}
              onClick={onClose}
              data-testid="close-modal"
            />
          )}
        </div>
        <div className="max-h-[70vh] overflow-y-auto px-4 py-4">{children}</div>
        {footer !== undefined && (
          <div className="flex items-center justify-end gap-2 border-t border-line px-4 py-3">{footer}</div>
        )}
      </div>
    </div>,
    document.body,
  );
}

/** Confirmation for a destructive or irreversible action. */
export function ConfirmDialog({
  open,
  onCancel,
  onConfirm,
  title,
  body,
  confirmLabel = 'Confirm',
  tone = 'danger',
  isBusy = false,
}: {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  title: string;
  body: ReactNode;
  confirmLabel?: string;
  tone?: 'danger' | 'primary';
  isBusy?: boolean;
}): JSX.Element | null {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      size="sm"
      data-testid="confirm-dialog"
      footer={
        <>
          <button
            type="button"
            onClick={onCancel}
            className="h-control-md rounded-md border border-line-control px-3 text-row text-content hover:bg-surface-hover"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isBusy}
            data-testid="confirm-action"
            className={cn(
              'h-control-md rounded-md px-3 text-row font-medium disabled:opacity-60',
              tone === 'danger'
                ? 'bg-danger text-danger-fg hover:bg-danger-hover'
                : 'bg-interactive text-interactive-fg hover:bg-interactive-hover',
            )}
          >
            {confirmLabel}
          </button>
        </>
      }
    >
      <div className="text-row text-content">{body}</div>
    </Modal>
  );
}

/** Right-hand slide-over, used for the notification panel and quick detail views. */
export function Drawer({
  open,
  onClose,
  title,
  children,
  width = 'w-[400px]',
  'data-testid': testId,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  width?: string;
  'data-testid'?: string;
}): JSX.Element | null {
  useEffect(() => {
    if (!open) return undefined;
    const handler = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-drawer">
      <div className="absolute inset-0 animate-fade-in bg-surface-overlay" onClick={onClose} aria-hidden />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : 'Panel'}
        data-testid={testId ?? 'drawer'}
        className={cn(
          'absolute right-0 top-0 flex h-full max-w-full animate-drawer-in flex-col border-l border-line bg-surface shadow-xl',
          width,
        )}
      >
        <div className="flex items-center justify-between gap-2 border-b border-line px-4 py-3">
          <h2 className="text-row font-semibold text-content">{title}</h2>
          <IconButton
            variant="ghost"
            size="sm"
            label="Close panel"
            icon={<X className="h-4 w-4" />}
            onClick={onClose}
          />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      </aside>
    </div>,
    document.body,
  );
}
